/**
 * Calculate the Kelly fraction for a bet
 * @param {number} odds - The decimal odds of the bet
 * @param {number} fairOdds - The fair odds of the bet
 * @returns {number} The Kelly fraction (between 0 and 1)
 */
export const calculateKellyFraction = (odds, fairOdds) => {
  const b = odds - 1; // Decimal odds minus 1
  const p = 1 / fairOdds; // Probability of winning based on fair odds
  const q = 1 - p; // Probability of losing
  const kellyFraction = (p * b - q) / b;
  return Math.max(0, kellyFraction); // Never return negative Kelly
};

/**
 * Calculate the Kelly stake amount
 * @param {number} kellyPercentage - The percentage of Kelly to use (25 for quarter, 50 for half, 100 for full)
 * @param {number} odds - The decimal odds of the bet
 * @param {number} fairOdds - The fair odds of the bet
 * @param {Object} settings - Kelly settings object
 * @param {number} settings.bankroll - The bankroll amount
 * @param {boolean} settings.roundStakes - Whether to round stakes
 * @param {string} settings.roundTo - The rounding increment ("5" or "10")
 * @returns {string} Formatted stake amount with currency symbol
 */
export const calculateKellyStake = (
  kellyPercentage,
  odds,
  fairOdds,
  settings
) => {
  const kellyFraction = calculateKellyFraction(odds, fairOdds);
  let stake = kellyFraction * (kellyPercentage / 100); // Convert percentage to decimal

  if (settings?.bankroll) {
    stake = stake * settings.bankroll;

    if (settings.roundStakes && settings.roundTo) {
      const roundTo = parseInt(settings.roundTo);
      stake = Math.round(stake / roundTo) * roundTo;
    }

    return new Intl.NumberFormat("en-AU", {
      style: "currency",
      currency: "AUD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(stake);
  }

  // If no bankroll is set, return as a percentage
  return `${(stake * 100).toFixed(2)}%`;
};
